import React, { useState } from 'react';
import {
  BsPersonPlusFill,
  BsFiletypeCsv,
  BsStars,
  BsPlus,
  BsPeopleFill,
} from 'react-icons/bs';

import { ImportMethodSelector } from '@components/ImportMethodSelector';
import { CsvImportDropzone } from '@components/CsvImportDropzone';
import { EmployeeFormRow } from '@components/EmployeeFormRow';
import { AddedEmployeesList } from '@components/AddedEmployeesList';
import { Button as CustomButton, buttonVariants } from '@components/Button';
import { ROLE_OPTIONS, getAssignableRoleOptions } from '@/constants/roles';
import {
  makeEmptyEmployee,
  validateCsvRowsWithTeam,
} from '@/utils/employeeImport';
import { useTimezones } from '@/hooks/useTimezones';
import { useIsSuperAdmin } from '@/hooks/usePermission';
import styles from '../Onboarding.module.scss';

// Onboarding's own accent color (distinct from the app's default blue) —
// kept as-is so this step doesn't visually shift from the rest of the flow.
const ONBOARDING_ACCENT = '#3363F5';

const IMPORT_METHODS = [
  {
    key: 'invite',
    icon: <BsPersonPlusFill size={22} color="#3363F5" />,
    iconBg: '#eff4ff',
    title: 'Invite Employees',
    desc: 'Send invitation links to employees to join Prodaff.',
    enabled: true,
  },
  {
    key: 'csv',
    icon: <BsFiletypeCsv size={22} color="#039855" />,
    iconBg: '#ecfdf3',
    title: 'Import via CSV',
    desc: 'Upload a CSV file and onboard your entire department at once.',
    enabled: true,
  },
];

const CSV_TEMPLATE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'team', label: 'Department' },
  { key: 'role', label: 'Role' },
  { key: 'timeZone', label: 'Timezone' },
  { key: 'deskId', label: 'Desk ID' },
  { key: 'employeeId', label: 'Employee ID' },
];

const CSV_TEMPLATE_ROWS = [
  [
    'John Doe',
    'john.doe@company.com',
    'Engineering',
    'Member',
    'America/New_York',
    'DESK-001',
    'EMP-001',
  ],
  [
    'Jane Smith',
    'jane.smith@company.com',
    'Marketing',
    'Manager',
    'Europe/London',
    '',
    '',
  ],
];

// ── Main component ────────────────────────────────────────────

export const CreateEmployee = ({ form, setForm, error, onSkip }) => {
  const timezoneOptions = useTimezones();
  const isSuperAdmin = useIsSuperAdmin();
  const assignableRoleOptions = getAssignableRoleOptions(ROLE_OPTIONS, {
    isSuperAdmin,
  });

  const [selectedMethod, setSelectedMethod] = useState('invite');

  // Rows the user is currently filling in
  const [editingRows, setEditingRows] = useState([makeEmptyEmployee()]);

  // Confirmed employees (shown in "Added" list + synced into form.employees)
  const [invitedList, setInvitedList] = useState(() =>
    form.employees.filter((e) => e.name || e.email)
  );

  // Accumulate team/department options as users create new ones
  const [teamOptions, setTeamOptions] = useState(() => {
    const teams = new Set(form.employees.map((e) => e.team).filter(Boolean));
    return [...teams].map((t) => ({ label: t, value: t }));
  });

  // Per-row, per-field validation errors for the manual "Add Employee"
  // action, distinct from the `error` prop (which reflects the parent
  // step's Continue-click check) — keyed by editingRows index.
  const [rowFieldErrors, setRowFieldErrors] = useState({});

  // ── Handlers ──────────────────────────────────────────────

  const updateEditingRow = (index, field, value) => {
    setEditingRows((rows) => {
      const updated = [...rows];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    setRowFieldErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const rowErrors = { ...prev[index] };
      delete rowErrors[field];
      return { ...prev, [index]: rowErrors };
    });
  };

  const addTeamOption = (value) => {
    setTeamOptions((opts) => {
      if (opts.find((o) => o.value === value)) return opts;
      return [...opts, { label: value, value }];
    });
  };

  const addEditingRow = () => {
    setEditingRows((rows) => [...rows, makeEmptyEmployee()]);
  };

  const removeEditingRow = (index) => {
    setEditingRows((rows) =>
      rows.length === 1
        ? [makeEmptyEmployee()]
        : rows.filter((_, i) => i !== index)
    );
    setRowFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (i < index) next[i] = prev[i];
        else if (i > index) next[i - 1] = prev[i];
      });
      return next;
    });
  };

  const handleSendInvitation = () => {
    const nonBlankIndices = [];
    const nonBlankRows = [];
    editingRows.forEach((e, i) => {
      const isBlank = !(
        e.name ||
        e.email ||
        e.team ||
        e.timeZone ||
        e.role ||
        e.deskId ||
        e.employeeId
      );
      if (!isBlank) {
        nonBlankIndices.push(i);
        nonBlankRows.push(e);
      }
    });
    if (!nonBlankRows.length) return;

    const validated = validateCsvRowsWithTeam(nonBlankRows);

    const newFieldErrors = {};
    const validRows = [];
    const remainingRows = [];

    editingRows.forEach((row, i) => {
      const nonBlankPos = nonBlankIndices.indexOf(i);
      if (nonBlankPos === -1) {
        remainingRows.push(row);
        return;
      }
      const { _errors, _fieldErrors, ...validatedEmp } = validated[nonBlankPos];
      if (_errors.length === 0) {
        validRows.push(validatedEmp);
      } else {
        newFieldErrors[remainingRows.length] = _fieldErrors;
        remainingRows.push(validatedEmp);
      }
    });

    setRowFieldErrors(newFieldErrors);
    if (validRows.length) {
      const newList = [...invitedList, ...validRows];
      setInvitedList(newList);
      setForm({ ...form, employees: newList });
    }
    setEditingRows(
      remainingRows.length ? remainingRows : [makeEmptyEmployee()]
    );
  };

  const handleImportFromCsv = ({ validRows }) => {
    const newList = [...invitedList, ...validRows];
    setInvitedList(newList);
    setForm({ ...form, employees: newList });
  };

  const removeInvitedEmployee = (index) => {
    const newList = invitedList.filter((_, i) => i !== index);
    setInvitedList(newList);
    setForm({ ...form, employees: newList });
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className={styles.inviteEmpPage}>
      {/* Import method selector */}
      <ImportMethodSelector
        methods={IMPORT_METHODS}
        selectedMethod={selectedMethod}
        onSelect={setSelectedMethod}
        accentColor={ONBOARDING_ACCENT}
      />

      {/* Pro tip banner */}
      <div className={styles.proTipBanner}>
        <BsStars size={15} color="#6938EF" />
        <span>
          <strong>Pro tip:</strong> Most companies invite at least 5 employees
          during setup.
        </span>
        <div className={styles.proTipBtnWrapper}>
          <button type="button" className={styles.proTipBtn}>
            Why is this important? ⓘ
          </button>
          <div className={styles.proTipTooltip} role="tooltip">
            <p className={styles.proTipTooltipHeading}>
              Why invite employees early?
            </p>
            <div className={styles.proTipTooltipItems}>
              <div className={styles.proTipTooltipItem}>
                <span
                  className={`${styles.proTipTooltipDot} ${styles.proTipDotBlue}`}
                />
                <span>
                  Teams onboarded early adopt the tool{' '}
                  <strong>3× faster</strong>
                </span>
              </div>
              <div className={styles.proTipTooltipItem}>
                <span
                  className={`${styles.proTipTooltipDot} ${styles.proTipDotGreen}`}
                />
                <span>
                  Employees start <strong>tracking from day one</strong>, no
                  manual catch-up
                </span>
              </div>
              <div className={styles.proTipTooltipItem}>
                <span
                  className={`${styles.proTipTooltipDot} ${styles.proTipDotPurple}`}
                />
                <span>
                  More members = <strong>richer productivity insights</strong>{' '}
                  for reports
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Employee form */}
      <div className={styles.addEmpSection}>
        <div className={styles.addEmpSectionHeader}>
          <h3 className={styles.addEmpSectionTitle}>
            {selectedMethod === 'csv' ? 'Import Employees' : 'Add Employee'}
          </h3>
          <p className={styles.addEmpSectionSubtitle}>
            {selectedMethod === 'csv'
              ? 'Upload a CSV file to add your entire department at once.'
              : 'Invite department members one by one.'}
          </p>
        </div>

        {selectedMethod === 'csv' ? (
          <CsvImportDropzone
            onImport={handleImportFromCsv}
            previewColumns={CSV_TEMPLATE_COLUMNS}
            templateFilename="employees_template.csv"
            templateRows={CSV_TEMPLATE_ROWS}
            validateRows={validateCsvRowsWithTeam}
          />
        ) : (
          <>
            <div className={styles.empFormList}>
              {editingRows.map((row, i) => (
                <EmployeeFormRow
                  key={i}
                  row={row}
                  index={i}
                  timezoneOptions={timezoneOptions}
                  roleOptions={assignableRoleOptions}
                  onChange={updateEditingRow}
                  onRemove={removeEditingRow}
                  canRemove={editingRows.length > 1}
                  showDepartment
                  departmentOptions={teamOptions}
                  onAddDepartment={addTeamOption}
                  errors={rowFieldErrors[i] || {}}
                />
              ))}
            </div>

            <div className={styles.empFormActions}>
              <CustomButton
                text="Add Another Employee"
                icon={<BsPlus size={18} />}
                variant={buttonVariants.ghost}
                onClick={addEditingRow}
              />
              <button
                type="button"
                className={styles.sendInviteBtn}
                onClick={handleSendInvitation}
              >
                Add Employee
              </button>
            </div>
          </>
        )}
      </div>

      {/* Added employees list */}
      <AddedEmployeesList
        employees={invitedList}
        onRemove={removeInvitedEmployee}
      />

      {/* Validation error */}
      {error && (
        <div className={styles.empError}>
          <pre>{error}</pre>
        </div>
      )}

      {/* Skip banner */}
      <div className={styles.skipBanner}>
        <BsPeopleFill size={18} color="#98A2B3" />
        <span className={styles.skipBannerText}>
          You can always invite more people later from the dashboard.
        </span>
        {/* <button type="button" className={styles.skipNowBtn} onClick={onSkip}>
          Skip for Now
        </button> */}
      </div>
    </div>
  );
};
